import React, { useMemo, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle, GridDirectedLineChart } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { useGetOverviewReviewsExplanations } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetricsExplanations';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import {
  apiToLineChartDataTransformer,
  PerformanceReviewGraphV1Wrapper,
} from 'Deer/pages/MyData/Overview/cards/ReviewsOverview/PerformanceReviewGraphV1Wrapper';
import { apiToGridDirectedChartDataTransformer } from 'Deer/pages/MyData/Overview/cards/ReviewsOverview/apiToGridDirectedChartDataTransformer';
import { useGetGridChartData } from 'Deer/pages/MyData/Overview/cards/ReviewsOverview/dataHooks';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { cloneDeep, isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { checkNewStaticDataFeaturesEnabled, checkSandboxStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

export const getGridTopToBottomFlatOrderData = (boxDataItem) => {
  const copiedBoxDataItem = cloneDeep(boxDataItem);
  // this function is used to get the data in top to bottom order to render visually in grid
  return copiedBoxDataItem.reverse().flat();
};

export const boxData = [
  [
    {
      color: '#FAD4D4',
      count: 0,
      label: 'Risk',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 0,
          maxScore: 1.5,
        },
        POTENTIAL: {
          minScore: 0,
          maxScore: 1.5,
        },
      },
    },
    {
      color: '#EAEAEA',
      count: 2,
      label: 'Average',
      percentage: 0.12,
      scoreRange: {
        PERFORMANCE: {
          minScore: 1.5,
          maxScore: 3.5,
        },
        POTENTIAL: {
          minScore: 0,
          maxScore: 1.5,
        },
      },
    },
    {
      color: '#FFE7C5',
      count: 0,
      label: 'Solid performer',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 3.5,
          maxScore: 5,
        },
        POTENTIAL: {
          minScore: 0,
          maxScore: 1.5,
        },
      },
    },
  ],
  [
    {
      color: '#EAEAEA',
      count: 0,
      label: 'Inconsistent player',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 0,
          maxScore: 1.5,
        },
        POTENTIAL: {
          minScore: 1.5,
          maxScore: 3.5,
        },
      },
    },
    {
      color: '#FFE7C5',
      count: 0,
      label: 'Core player',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 1.5,
          maxScore: 3.5,
        },
        POTENTIAL: {
          minScore: 1.5,
          maxScore: 3.5,
        },
      },
    },
    {
      color: '#E6F1FD',
      count: 0,
      label: 'High performer',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 3.5,
          maxScore: 5,
        },
        POTENTIAL: {
          minScore: 1.5,
          maxScore: 3.5,
        },
      },
    },
  ],
  [
    {
      color: '#FFE7C5',
      count: 0,
      label: 'Potential gem',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 0,
          maxScore: 1.5,
        },
        POTENTIAL: {
          minScore: 3.5,
          maxScore: 5,
        },
      },
    },
    {
      color: '#E6F1FD',
      count: 0,
      label: 'High Potential',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 1.5,
          maxScore: 3.5,
        },
        POTENTIAL: {
          minScore: 3.5,
          maxScore: 5,
        },
      },
    },
    {
      color: '#DBF2D3',
      count: 0,
      label: 'Star',
      percentage: 0,
      scoreRange: {
        PERFORMANCE: {
          minScore: 3.5,
          maxScore: 5,
        },
        POTENTIAL: {
          minScore: 3.5,
          maxScore: 5,
        },
      },
    },
  ],
];

const commonArgs = {
  xAxisLabel: 'Performance (Goal score)',
  yAxisLabel: 'Potential (Competency score)',
  connectNulls: true,
  tooltipTitle: 'Average Score',
  withBackground: false,
};

const employee1 = {
  id: '1',
  name: 'Helen Mirren',
  email: 'someEmail@gmail.com',
  imageUrl: 'https://media.mesh.ai/2415/media/profilePhoto/50702/2023/01/06/50702.jpg_1673027580262',
};
const employee2 = {
  id: '2',
  name: 'Ming Ye',
  email: 'someEmail@gmail.com',
  imageUrl: 'https://media.mesh.ai/2415/media/profilePhoto/50724/2023/01/06/50724.jpg_1673027627036',
};
const employee3 = {
  id: '3',
  name: 'George Weber',
  email: 'someEmail@gmail.com',
  imageUrl: 'https://media.mesh.ai/2415/media/profilePhoto/50723/2023/01/06/50723.jpg_1673027618924',
};

const employee5 = {
  id: '5',
  name: 'Jay Chandra',
  email: 'someEmail@gmail.com',
  imageUrl: 'https://media.mesh.ai/2415/media/profilePhoto/50721/2023/01/06/50721.jpg_1673027588228',
};

export const data2dBothNumeric = {
  nodes: [
    {
      id: '1',
      xValues: [2.3, 2, 3],
      yValues: [2.2, 3.4, 4],
      pointsMetaData: [
        { reviewCycle: { id: '1', name: 'Annual Performance Review 2022 ' } },
        { reviewCycle: { id: '2', name: 'Mid Year Performance Review 2023' } },
        { reviewCycle: { id: '3', name: 'Annual Performance Review 2023' } },
      ],
      seriesMetaData: { employee: employee1 },
    },
    {
      id: '2',
      xValues: [1.3, 1.75, 2.5],
      yValues: [2.3, 2.34, 3],
      pointsMetaData: [
        { reviewCycle: { id: '1', name: 'Annual Performance Review 2022 ' } },
        { reviewCycle: { id: '2', name: 'Mid Year Performance Review 2023' } },
        { reviewCycle: { id: '3', name: 'Annual Performance Review 2023' } },
      ],
      seriesMetaData: { employee: employee2 },
    },
    {
      id: '3',
      xValues: [4, 3.2, 3.5],
      yValues: [3.2, 2.2, 3.75],
      pointsMetaData: [
        { reviewCycle: { id: '1', name: 'Annual Performance Review 2022 ' } },
        { reviewCycle: { id: '2', name: 'Mid Year Performance Review 2023' } },
        { reviewCycle: { id: '3', name: 'Annual Performance Review 2023' } },
      ],
      seriesMetaData: { employee: employee3 },
    },
    {
      id: '5',
      xValues: [2.5, 2.87, 4.2],
      yValues: [1.2, 2.8, 1.9],
      pointsMetaData: [
        { reviewCycle: { id: '1', name: 'Annual Performance Review 2022 ' } },
        { reviewCycle: { id: '2', name: 'Mid Year Performance Review 2023' } },
        { reviewCycle: { id: '3', name: 'Annual Performance Review 2023' } },
      ],
      seriesMetaData: { employee: employee5 },
    },
  ],
};

const props = {
  directedLineChartProps: {
    ...commonArgs,
    nodes: data2dBothNumeric.nodes,
    yAxisDomain: [0, 5],
    xAxisDomain: [0, 5],
  },
  gridChartProps: {
    boxData: getGridTopToBottomFlatOrderData(boxData),

    rows: 3,
    columns: 3,
    boxHeight: '90px',
    position: 'absolute',
    margin: '0',
    width: '100%',
    left: 0,
    top: 0,
    isExpanded: false,
    isTextCenterAlign: false,
  },
};

export const DirectReportsReviewsOverview = () => {
  const history = useHistory();

  const dispatch = useDispatch();
  const currentURL = history.location.pathname;
  const [correlationId] = useState(window.crypto.randomUUID());

  const { data, isLoading, isError, isFetched } = useGetGridChartData({
    cohortToPass: {
      cohort: {
        id: -2,
        name: 'My Team',
        type: 'MY_TEAM',
      },
      groupByKeys: null,
      splitByKeys: null,
      orderBy: null,
      parentUuid: null,
      timeBounds: [],
    },
  });

  const { gridDefinition = [], employeeReviewCycleData = [], apiData } = data ?? {};
  const is1dVersion = gridDefinition?.length === 1;

  const chartProps = useMemo(() => {
    const transformedData = is1dVersion
      ? apiToLineChartDataTransformer({ nodes: employeeReviewCycleData })
      : apiToGridDirectedChartDataTransformer(employeeReviewCycleData);

    if (is1dVersion) {
      return transformedData;
    }
    return {
      ...commonArgs,
      nodes: transformedData,
      yAxisDomain: [0, 5],
      xAxisDomain: [0, 5],
    };
  }, [employeeReviewCycleData, is1dVersion]);

  let isEmptyData = employeeReviewCycleData.length === 0 || isEmpty(gridDefinition);
  if (is1dVersion) {
    isEmptyData = isEmptyData || chartProps?.performanceReviewData?.reviewRatingDetailsResponseList?.length === 0;
  } else {
    isEmptyData = isEmptyData || chartProps?.nodes?.length === 0;
  }

  const gridChartPropsApi = useMemo(() => {
    // eslint-disable-next-line no-shadow
    const boxData = getGridTopToBottomFlatOrderData(gridDefinition);

    return {
      boxData,
      rows: gridDefinition.length,
      columns: gridDefinition?.[0]?.length,
      boxHeight: '90px',
      position: 'absolute',
      margin: '0',
      width: '100%',
      left: 0,
      top: 0,
      isExpanded: false,
      isTextCenterAlign: false,
    };
  }, [gridDefinition]);

  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myDirectTeamReviewsOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'reviewsOverview',
    section: 'myDirectTeam',
  });

  const gridData = useMemo(
    () => ({
      chartData: boxData,
    }),
    []
  );

  const gridBoxData = useMemo(() => {
    if (!gridData) return [[]];
    return getGridTopToBottomFlatOrderData(gridData.chartData);
  }, [gridData]);

  const rows = gridData?.chartData?.length || 0;
  const columns = gridData?.chartData?.[0]?.length || 0;

  const staticGridChartProps = useMemo(
    () => ({
      boxData: gridBoxData,
      rows,
      columns,
      // boxHeight: '90px',
      position: 'absolute',
      margin: '0',
      width: '100%',
      left: 0,
      top: 0,
      isExpanded: false,
      isTextCenterAlign: false,
    }),
    [gridBoxData, rows, columns]
  );

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'reviewsOverview',
        section: 'myDirectTeam',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  const { data: explanationData } = useGetOverviewReviewsExplanations({
    cohortId: -2,
    group: 'REVIEW',
    correlationId, // currently not used like other modules, sending for future use
    enabled: isFetched,
    gridLineGraphResponse: apiData,
  });

  const explanation: {
    explanation?: string;
    sentiment?: 'warning' | 'success';
  } = useMemo(
    () =>
      explanationData && explanationData[0]
        ? {
            explanation: explanationData[0].explanation,
            sentiment: explanationData[0].sentiment === 'negative' ? 'warning' : 'success',
          }
        : {},
    [explanationData]
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <OverviewSection
      open={isCardOpen}
      onOpenChange={handleChange}
      title="Performance outcomes"
      showCaption={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() || explanation.explanation
      }
      subTitleColor={explanation.sentiment ?? 'success'}
      subTitle={
        explanation.explanation ??
        'Jay and George are excelling in goal delivery, while Ming may need extra support. Consider discussing with them in your next 1:1 how you can support them in overcoming these obstacles.'
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Performance grid</CardTitle>
        </CardHeader>
        <CardContent>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isError || isEmptyData ? (
            <CardContent
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EmptyScreen
                illustrationSrc={noInsights}
                heading="Not enough data to show insights"
                isSmallTextSize
                description="Insights on your team's progress would unlock as you complete review cycles"
                smallTextMargin="0px"
                illustrationStyle={{
                  width: '180px',
                }}
              />
            </CardContent>
          ) : is1dVersion ? (
            <div style={{ height: '230px' }}>
              <PerformanceReviewGraphV1Wrapper lineChartData={chartProps} />
            </div>
          ) : (
            <div style={{ height: '230px' }}>
              {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
                <GridDirectedLineChart {...props} gridChartProps={staticGridChartProps} mode="full" />
              ) : (
                <GridDirectedLineChart
                  directedLineChartProps={chartProps}
                  gridChartProps={gridChartPropsApi}
                  mode="full"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </OverviewSection>
  );
};
